<template>
  <div class="col-3 c-px-3 c-py-2">
    <h3 class="text-lg-c text-primary">
      {{ t('top_product') }}
    </h3>

    <div
      v-if="mainProduct"
      class="flex text-center c-flex-col relative"
    >
      <div class="embed-responsive c-mb-0 c-flex-grow-1">
        <RemoteResponsiveImage
          :product-image-data="mainProduct.images[0]?.data"
          img-class="embed-responsive-item relative image-cover"
          :alt="mainProduct.name"
          sizes="10vw"
        />
      </div>
      <router-link
        v-close-popper
        class="c-mb-5 stretched-link z-1"
        :to="{ name: 'productPath', params: { permalink: mainProduct.permalink } }"
      >
        <h4 class="text-primary c-mt-4 c-mb-2">
          {{ mainProduct.name }}
        </h4>
        <strong class="h2 text-primary font-bold">
          <template v-if="mainProduct.quantityDiscounts">
            {{
              discountPriceFormatter(
                showFromLabel(mainProduct.quantityDiscounts, mainProduct.plusClubPrice) ? t('from') : '',
                mainProduct.plusClubPrice,
                getCurrentCustomer?.inPlusClub,
                mainProduct.quantityDiscounts[0].price,
                mainProduct.quantityDiscounts[mainProduct.quantityDiscounts.length - 1].price,
                mainProduct.currency,
              )
            }}
          </template>
          <template v-else>{{ numberToCurrency(mainProduct.price, mainProduct.currency) }}</template>
        </strong>
      </router-link>

      <div class="z-2">
        <AddToCartButton
          v-if="!mainProduct.isIngredient && mainProduct.state === 'available'"
          :product="mainProduct"
          gtm-source="top-search"
        />
        <router-link
          v-else
          v-close-popper
          :to="{ name: 'productPath', params: { permalink: mainProduct.permalink } }"
          type="button"
          class="btn btn-warning c-p-3 c-sm:c-p-2 stretched-link c-rounded-circle"
          data-toggle="product-detail-modal"
          aria-label="product info"
        >
          <i class="mi-info icon-20" />
        </router-link>
      </div>
    </div>
  </div>
  <div
    v-if="products.length > 0"
    class="col-6 c-pr-0 border-left border-primary-20 c-pl-3 c-py-2 c-pr-1"
  >
    <h3 class="text-lg-c text-primary">
      {{ t('other_products') }}
    </h3>
    <div class="search-results">
      <ul class="list-unstyled c-m-0 row search-results-desktop">
        <li
          v-for="(product, index) in products"
          :key="product.id"
          class="flex c-py-1 c-px-1 c-pr-3 col-6"
          :class="{ 'mt-4': index > 1 }"
        >
          <div class="row c-flex-grow-1 c-items-start">
            <div class="col-4 c-px-1">
              <router-link
                v-close-popper
                :to="{ name: 'productPath', params: { permalink: product.permalink } }"
              >
                <div
                  v-if="product.images.length > 0"
                  class="embed-responsive c-mb-0 c-flex-grow-1"
                >
                  <RemoteResponsiveImage
                    :product-image-data="product.images[0]?.data"
                    img-class="embed-responsive-item relative image-cover"
                    :alt="product.name"
                    sizes="10vw"
                  />
                </div>
              </router-link>
            </div>
            <div class="col-8 c-px-1 relative">
              <router-link
                v-close-popper
                class="block c-p-1 text-sm-c c-flex-grow-1 c-leading-sm stretched-link relative z-1"
                :to="{ name: 'productPath', params: { permalink: product.permalink } }"
              >
                <span>{{ product.name }}</span>
                <strong class="text-md-c">
                  <template v-if="product.quantityDiscounts">
                    {{
                      discountPriceFormatter(
                        showFromLabel(product.quantityDiscounts, product.plusClubPrice) ? t('from') : '',
                        product.plusClubPrice,
                        getCurrentCustomer?.inPlusClub,
                        product.quantityDiscounts[0].price,
                        product.quantityDiscounts[product.quantityDiscounts.length - 1].price,
                        product.currency,
                      )
                    }}
                  </template>
                  <template v-else>{{ numberToCurrency(product.price, product.currency) }}</template>
                </strong>
              </router-link>
              <AddToCartButton
                v-if="!product.isIngredient && product.state === 'available'"
                class="w-auto c-float-right relative z-2"
                :product="product"
                :icon-button-only="true"
                gtm-source="navbar-products-search"
              />
              <router-link
                v-else
                v-close-popper
                :to="{ name: 'productPath', params: { permalink: product.permalink } }"
                type="button"
                class="btn btn-warning c-p-3 c-sm:c-p-2 stretched-link c-rounded-circle c-float-right relative z-2"
                data-toggle="product-detail-modal"
                aria-label="product info"
              >
                <i class="mi-info icon-20" />
              </router-link>
            </div>
          </div>
        </li>
      </ul>
      <div
        v-if="showAllResults"
        class="text-center c-mt-5"
      >
        <button
          class="btn  btn-primary c-px-5 c-md:c-px-3 c-xl:c-px-5 font-medium c-rounded-pill"
          type="button"
          @click="goToSearchView"
        >
          {{ t('show_all_results') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import AddToCartButton from '@/components/buttons/ButtonAddToCartButton.vue'
import RemoteResponsiveImage from '@/components/general/BaseRemoteResponsiveImage.vue'
import { discountPriceFormatter } from '@/mixins/discountPriceFormatter'
import { numberToCurrency } from '@/mixins/numberToCurrency'
import { useAppStore } from '@/stores/AppStore'

const appStore = useAppStore()
const { getCurrentCustomer } = storeToRefs(appStore)

const props = defineProps({
  mainProduct: {
    type: Object,
    required: true,
  },
  products: {
    type: Array,
    required: true,
  },
  searchText: {
    type: String,
    default: '',
  },
  showAllResults: {
    type: Boolean,
    default: true,
  },
})

const { t } = useI18n()
const router = useRouter()

const goToSearchView = () => {
  const url = router.resolve({ name: 'searchPath', params: { search: props.searchText } })
  window.location.href = url.href
}

// Defines if "From [some price]" label should be visible.
const showFromLabel = (quantityDiscounts, plusClubPrice) => {
  return quantityDiscounts && (!plusClubPrice || !getCurrentCustomer.value?.inPlusClub)
}
</script>

<i18n>
  {
    "cs": {
    "from": "Od",
    "other_products": "Další dobroty",
    "show_all_results": "Zobrazit všechny výsledky",
    "top_product": "Top Vyhledávání"
},
    "sk": {
    "from": "Od",
    "other_products": "Ďalšie dobroty",
    "show_all_results": "Zobraziť všetky výsledky",
    "top_product": "Top produkt"
},
    "pl": {
    "from": "Od",
    "other_products": "Inne smakołyki",
    "show_all_results": "Zobacz wszystkie wyniki",
    "top_product": "Super produkt"
},
    "hu": {
    "from": "Ft-tól",
    "other_products": "Egyéb finomságok",
    "show_all_results": "Összes eredmény megtekintése",
    "top_product": "Kedvenc termék"
},
    "de": {
    "from": "Von",
    "other_products": "Mehr Leckereien",
    "show_all_results": "Alle Ergebnisse anzeigen",
    "top_product": "Top-Suche"
},
    "at": {
    "from": "Von",
    "other_products": "Mehr Leckereien",
    "show_all_results": "Alle Ergebnisse anzeigen",
    "top_product": "Top-Suche"
},
    "ro": {
    "from": "De la",
    "other_products": "Alte bunătăți",
    "show_all_results": "Vezi toate rezultatele",
    "top_product": "Produs grozav"
},
    "fr": {
    "from": "À partir de",
    "other_products": "Autres gourmandises",
    "show_all_results": "Afficher tous les résultats",
    "top_product": "Top recherches"
},
    "it": {
    "from": "Da",
    "other_products": "Altre prelibatezze",
    "show_all_results": "Mostra tutti i risultati",
    "top_product": "Ricerca top"
}
  }
</i18n>
